import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TableLoading } from '../Loading';
import { User as RoleBasedUser, getUsersWithFiltersByRole } from '../../services/roleBasedUsers.service';
import { formatLoginTime } from '../../utils/helpers/date.helpers';
import { formatNumberWithCommas } from '../../utils/helpers/formatting.helpers';
import QRCodePopup from '../QRCodePopup/QRCodePopup';
import { getAllRoles, Role } from '../../services/roles.service';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { Pagination, PaginationItem, Stack } from '@mui/material';
import { ReactComponent as SortIcon } from '../../assets/icons/sort.svg';
import { authService } from '../../services/auth.service';
import { updateUserService } from '../../services/updateUser.service';
import {
  startAllBalancesSubscription,
  stopAllBalancesSubscription,
  AllBalancesUpdatedData,
} from '../../services/allBalancesSubscription.service';
import {
  startTotalsSubscription,
  stopTotalsSubscription,
  TotalsUpdatedData,
} from '../../services/totalsSubscription.service';
import { RoleConfig } from './roleConfig';

type Sorting = 'desc' | 'asc' | 'def';

interface RoleListPageProps {
  config: RoleConfig;
  styles: Record<string, string>;
}

function getLastWeekDate() {
  const today = new Date();
  const lastWeek = new Date(today);
  lastWeek.setDate(today.getDate() - 7);
  const year = lastWeek.getFullYear();
  const month = String(lastWeek.getMonth() + 1).padStart(2, '0');
  const day = String(lastWeek.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const balanceTotalAmount = (
  b: RoleBasedUser['Balance'] | undefined,
  includeOnHold: boolean,
) => {
  if (!b) return 0;
  const amount = Number(b.amount || 0);
  return includeOnHold ? amount + Number(b.onHold || 0) : amount;
};

const RoleListPage: React.FC<RoleListPageProps> = ({ config, styles }) => {
  const navigate = useNavigate();
  const [nameSearch, setNameSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(getLastWeekDate());
  const [dateTo, setDateTo] = useState(formatDate(new Date()));
  const [timeFrom, setTimeFrom] = useState('00:00');
  const [timeTo, setTimeTo] = useState('23:59');
  const [users, setUsers] = useState<RoleBasedUser[]>();
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [realtimeTotals, setRealtimeTotals] = useState<TotalsUpdatedData | null>(null);
  const [sortedUsers, setSortedUsers] = useState<RoleBasedUser[]>();
  const [nameSearchTimeout, setNameSearchTimeout] = useState<any>(null);

  const [pageInfo, setPageInfo] = useState<{
    startCursor?: string;
    endCursor?: string;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    total: number;
  }>({
    startCursor: undefined,
    endCursor: undefined,
    hasNextPage: false,
    hasPreviousPage: false,
    total: 0,
  });
  const [cursors, setCursors] = useState<{ after?: string }[]>([
    { after: undefined },
  ]);
  const [qrPopupOpen, setQrPopupOpen] = useState(false);
  const [selectedAffiliateId, setSelectedAffiliateId] = useState<string>('');
  const [selectedUsername, setSelectedUsername] = useState<string>('');
  const PAGE_SIZE = 10;
  const [allRoles, setAllRoles] = useState<Role[]>([]);
  const { environments, selectedEnvironmentId, setSelectedEnvironmentId } = useEnvironment();
  const [siblingCount, setSiblingCount] = useState(2);

  const [sortingByBalance, setSortingByBalance] = useState<Sorting>('def');
  const [sortingByIn, setSortingByIn] = useState<Sorting>('def');
  const [sortingByOut, setSortingByOut] = useState<Sorting>('def');
  const [sortingByProfit, setSortingByProfit] = useState<Sorting>('def');
  const [sortingByPlayingGame, setSortingByPlayingGame] = useState<Sorting>('def');

  useEffect(() => { setSortedUsers(sortByBalance(sortingByBalance)); }, [sortingByBalance, users]);
  useEffect(() => { setSortedUsers(sortByIn(sortingByIn)); }, [sortingByIn, users]);
  useEffect(() => { setSortedUsers(sortByOut(sortingByOut)); }, [sortingByOut, users]);
  useEffect(() => { setSortedUsers(sortByProfit(sortingByProfit)); }, [sortingByProfit, users]);
  useEffect(() => {
    if (config.showPlayingGameColumn) {
      setSortedUsers(sortByPlayingGame(sortingByPlayingGame));
    }
  }, [sortingByPlayingGame, users]);

  const sortByBalance = (sorting: Sorting) => {
    if (!users) return;
    if (sorting === 'def') return users;
    const get = (u: RoleBasedUser) => balanceTotalAmount(u.Balance, config.balanceIncludesOnHold);
    return sorting === 'desc'
      ? [...users].sort((a, b) => get(b) - get(a))
      : [...users].sort((a, b) => get(a) - get(b));
  };
  const sortByIn = (sorting: Sorting) => {
    if (!users) return;
    if (sorting === 'def') return users;
    return sorting === 'desc'
      ? [...users].sort((a, b) => (b.balanceIn || 0) - (a.balanceIn || 0))
      : [...users].sort((a, b) => (a.balanceIn || 0) - (b.balanceIn || 0));
  };
  const sortByOut = (sorting: Sorting) => {
    if (!users) return;
    if (sorting === 'def') return users;
    return sorting === 'desc'
      ? [...users].sort((a, b) => (b.balanceOut || 0) - (a.balanceOut || 0))
      : [...users].sort((a, b) => (a.balanceOut || 0) - (b.balanceOut || 0));
  };
  const sortByProfit = (sorting: Sorting) => {
    if (!users) return;
    if (sorting === 'def') return users;
    const profit = (u: RoleBasedUser) =>
      (u.balanceIn ? Number(u.balanceIn) : 0) - (u.balanceOut ? Number(u.balanceOut) : 0);
    return sorting === 'desc'
      ? [...users].sort((a, b) => profit(b) - profit(a))
      : [...users].sort((a, b) => profit(a) - profit(b));
  };
  const sortByPlayingGame = (sorting: Sorting) => {
    if (!users) return;
    if (sorting === 'def') return users;
    const playing = (u: RoleBasedUser) => (u.Balance?.onHold ? 1 : 0);
    return sorting === 'desc'
      ? [...users].sort((a, b) => playing(b) - playing(a))
      : [...users].sort((a, b) => playing(a) - playing(b));
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 890) setSiblingCount(0);
      else if (window.innerWidth < 1080) setSiblingCount(1);
      else setSiblingCount(2);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const calculatedTotals = (users || []).reduce(
    (acc, d) => {
      acc.balance += balanceTotalAmount(d.Balance, config.balanceIncludesOnHold);
      acc.in += d.balanceIn ? Number(d.balanceIn) : 0;
      acc.out += d.balanceOut ? Number(d.balanceOut) : 0;
      acc.profit += (d as any).profit ? Number((d as any).profit) : 0;
      return acc;
    },
    { balance: 0, in: 0, out: 0, profit: 0 },
  );

  const totals = realtimeTotals
    ? {
        balance: realtimeTotals.totalBalance,
        in: realtimeTotals.totalBalanceIn,
        out: realtimeTotals.totalBalanceOut,
        profit: realtimeTotals.totalProfit,
      }
    : calculatedTotals;

  const buildAdditionalFilters = () => {
    const filters: any = {};
    if (nameSearch.trim()) filters.search = nameSearch.trim();
    if (dateFrom) filters.startDate = timeFrom ? `${dateFrom}T${timeFrom}` : dateFrom;
    if (dateTo) filters.endDate = timeTo ? `${dateTo}T${timeTo}` : dateTo;
    if (selectedEnvironmentId) filters.environmentID = selectedEnvironmentId;
    return filters;
  };

  const fetchData = async (pageNumber: number = 1) => {
    setLoading(true);
    const additionalFilters = buildAdditionalFilters();
    try {
      const data = await getUsersWithFiltersByRole(
        config.graphqlRoleName,
        additionalFilters,
        { first: PAGE_SIZE, after: (page - 1) * PAGE_SIZE },
      );
      setUsers(data.users);
      setTotal(data.pageInfo.total || 0);
      setPageInfo(data.pageInfo);
      setRealtimeTotals({
        totalBalance: data.totalBalance,
        totalBalanceIn: data.totalBalanceIn,
        totalBalanceOut: data.totalBalanceOut,
        totalProfit: data.totalProfit,
      });
    } catch (err) {
      console.error(`Failed to fetch ${config.label}s:`, err);
    } finally {
      setLoading(false);
    }
  };

  useLayoutEffect(() => {
    (async () => {
      const roles = await getAllRoles();
      setAllRoles(roles);
    })();
  }, []);

  useEffect(() => { fetchData(1); }, []);
  useEffect(() => { fetchData(page); }, [page]);
  useEffect(() => {
    setPage(1);
    setCursors([{ after: undefined }]);
    fetchData(1);
  }, [selectedEnvironmentId]);

  useEffect(() => {
    const currentUser = authService.getStoredUser();
    if (currentUser && users && users.length > 0) {
      const userIds = users.map((u) => u.id);
      const additionalFilters = buildAdditionalFilters();
      startAllBalancesSubscription(
        { userIds, currentUserId: currentUser.id, from: additionalFilters.startDate, to: additionalFilters.endDate },
        {
          onUpdate: (updatedData: AllBalancesUpdatedData) => {
            setUsers((prev) => {
              if (!prev || !updatedData.users) return prev;
              return prev.map((u) => {
                const updated = updatedData.users.find((x: any) => x.id === u.id);
                if (!updated) return u;
                return {
                  ...u,
                  Balance: updated.Balance
                    ? {
                        id: updated.Balance.id,
                        amount: updated.Balance.amount,
                        credit: u.Balance?.credit ?? 0,
                        onHold: config.balanceIncludesOnHold
                          ? (updated.Balance?.onHold ?? 0)
                          : (u.Balance?.onHold ?? 0),
                      }
                    : u.Balance,
                  balanceIn: updated.balanceIn ?? u.balanceIn,
                  balanceOut: updated.balanceOut ?? u.balanceOut,
                  credit: updated.credit ?? u.credit,
                  isActive: updated.isActive ?? u.isActive,
                  lastLogin: updated.lastLogin ?? u.lastLogin,
                  loginIp: updated.loginIp ?? u.loginIp,
                };
              });
            });
          },
          onError: (error) => console.error(`${config.label}Page: balances subscription error:`, error),
          onConnect: () => {},
          onDisconnect: () => {},
        },
      );
    }
    return () => { stopAllBalancesSubscription(); };
  }, [users, dateFrom, dateTo, timeFrom, timeTo]);

  useEffect(() => {
    const currentUser = authService.getStoredUser();
    if (currentUser) {
      const additionalFilters = buildAdditionalFilters();
      const roleId = allRoles.find((r) => r.name === config.graphqlRoleName)?.id;
      if (!roleId) return;
      startTotalsSubscription(
        {
          parentId: currentUser.id,
          roleId,
          search: nameSearch.trim() || undefined,
          from: additionalFilters.startDate,
          to: additionalFilters.endDate,
        },
        {
          onUpdate: (updatedTotals: TotalsUpdatedData) => setRealtimeTotals(updatedTotals),
          onError: () => setRealtimeTotals(null),
          onConnect: () => {},
          onDisconnect: () => setRealtimeTotals(null),
        },
      );
    } else {
      setRealtimeTotals(null);
    }
    return () => {
      stopTotalsSubscription();
      setRealtimeTotals(null);
    };
  }, [nameSearch, dateFrom, dateTo, timeFrom, timeTo, allRoles]);

  const handleNameSearch = () => {
    setPage(1);
    setCursors([{ after: undefined }]);
    fetchData(1);
  };
  const handleNameKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleNameSearch();
  };
  const handleNameKeyUp = () => {
    if (nameSearchTimeout) clearTimeout(nameSearchTimeout);
    setNameSearchTimeout(setTimeout(() => handleNameSearch(), 700));
  };
  const handleDateSearch = () => {
    setPage(1);
    setCursors([{ after: undefined }]);
    fetchData(1);
  };

  const handleClearFilters = () => {
    setNameSearch('');
    setDateFrom('');
    setDateTo('');
    setTimeFrom('');
    setTimeTo('');
    setSelectedEnvironmentId('');
    setPage(1);
    setCursors([{ after: undefined }]);
    setLoading(true);
    import('../../services/getAllUsers.service').then(async ({ getAllUsersService }) => {
      try {
        const roleId = allRoles.find((r) => r.name === config.graphqlRoleName)?.id;
        const data = await getAllUsersService.getAllUsers(roleId, PAGE_SIZE);
        setUsers(data.users);
        setTotal(data.pageInfo.total || 0);
        setPageInfo(data.pageInfo);
      } catch (err) {
        console.error(`Failed to fetch ${config.label}s:`, err);
      } finally {
        setLoading(false);
      }
    });
  };

  const handleAddNew = () => navigate(config.createRoute);
  const handleAffiliateIdClick = (affiliateId: string, username: string) => {
    setSelectedAffiliateId(affiliateId);
    setSelectedUsername(username);
    setQrPopupOpen(true);
  };

  const handleWagerEnabledChange = (id: number) => {
    const cur = users?.find((u) => u.id === id);
    const isWagerEnabled = cur ? !cur.isWagerEnabled : false;
    if (users) {
      setUsers(users.map((u) => (u.id === id ? { ...u, isWagerEnabled } : u)));
    }
    updateUserService.updateUser({ id, isWagerEnabled });
  };
  const handleCashbackEnabledChange = (id: number) => {
    const cur = users?.find((u) => u.id === id);
    const isCashbackEnabled = cur ? !cur.isCashbackEnabled : false;
    if (users) {
      setUsers(users.map((u) => (u.id === id ? { ...u, isCashbackEnabled } : u)));
    }
    updateUserService.updateUser({ id, isCashbackEnabled });
  };
  const handleOnlinePaymentEnabledChange = (id: number) => {
    const cur = users?.find((u) => u.id === id);
    const isOnlinePaymentEnabled = cur ? !cur.isOnlinePaymentEnabled : false;
    if (users) {
      setUsers(users.map((u) => (u.id === id ? { ...u, isOnlinePaymentEnabled } : u)));
    }
    updateUserService.updateUser({ id, isOnlinePaymentEnabled });
  };

  const cycleSort = (s: Sorting): Sorting => (s === 'asc' ? 'desc' : s === 'desc' ? 'asc' : 'asc');

  return (
    <main className={styles.main}>
      {authService.getStoredUser()?.role?.name === 'ADMIN' && (
        <div className={styles.environmentSelect}>
          <label className={styles.label}>Environment</label>
          <select
            className={styles.select}
            value={selectedEnvironmentId}
            onChange={(e) => setSelectedEnvironmentId(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">All Environments</option>
            {environments.map((environment) => (
              <option key={environment.id} value={environment.id}>
                {environment.name} - {environment.host}
              </option>
            ))}
          </select>
        </div>
      )}
      <h1 className={styles.title}>{config.pageTitle}</h1>
      <div className={styles.searchSection}>
        <div className={styles.searchByName}>
          <label className={styles.label}>{config.searchByLabel}</label>
          <input
            className={styles.input}
            placeholder="Please enter your search content"
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
            {...(config.searchMode === 'debounce-keyup'
              ? { onKeyUp: handleNameKeyUp }
              : { onKeyPress: handleNameKeyPress })}
          />
          <button className={styles.searchBtn} onClick={handleNameSearch}>Search</button>
          <button className={styles.addBtn} onClick={handleAddNew}>{config.addButtonLabel}</button>
        </div>
        <div className={styles.searchByDate}>
          <label className={styles.label}>Search by Date & Time</label>
          <div className={styles.dateInputGroup}>
            <span className={styles.dateIcon}>📅</span>
            <input type="date" className={styles.dateInput} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} placeholder="Start Date" />
            <input type="time" className={styles.timeInput} value={timeFrom} onChange={(e) => setTimeFrom(e.target.value)} placeholder="Start Time" />
            <span className={styles.toText}>To</span>
            <input type="date" className={styles.dateInput} value={dateTo} onChange={(e) => setDateTo(e.target.value)} placeholder="Finish Date" />
            <input type="time" className={styles.timeInput} value={timeTo} onChange={(e) => setTimeTo(e.target.value)} placeholder="Finish Time" />
          </div>
          <button className={styles.searchBtn} onClick={handleDateSearch}>Search</button>
          {(nameSearch || dateFrom || dateTo || timeFrom || timeTo || selectedEnvironmentId) && (
            <button className={styles.clearBtn} onClick={handleClearFilters}>Clear Filters</button>
          )}
          <button className={styles.addBtnMobile} onClick={handleAddNew}>{config.addButtonLabel}</button>
        </div>
      </div>
      <TableLoading
        loading={loading}
        rows={8}
        columns={11}
        text={config.loadingText}
        className={styles.tableWrapper}
      >
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{config.nameColumnLabel}</th>
                <th>{config.key === 'player' ? 'Username' : 'Login Account'}</th>
                <th>Role</th>
                <th>Status</th>
                {config.showWagerCashbackToggles && <th>Wager Enabled</th>}
                {config.showWagerCashbackToggles && <th>Cashback enabled</th>}
                {config.showOnlinePaymentToggle && <th>Online Payment Enabled</th>}
                <th>Affiliate ID</th>
                <th>Last Login Address</th>
                <th>Last Login Time</th>
                {config.showPlayingGameColumn && (
                  <th>
                    <div onClick={() => setSortingByPlayingGame(cycleSort)} className={styles.sortable}>
                      Playing Game <div className={styles.sortIcon}><SortIcon /></div>
                    </div>
                  </th>
                )}
                <th>
                  <div onClick={() => setSortingByBalance(cycleSort)} className={styles.sortable}>
                    Balance <div className={styles.sortIcon}><SortIcon /></div>
                  </div>
                </th>
                <th className={styles.inCol}>
                  <div onClick={() => setSortingByIn(cycleSort)} className={styles.sortable}>
                    In <div className={styles.sortIcon}><SortIcon /></div>
                  </div>
                </th>
                <th className={styles.outCol}>
                  <div onClick={() => setSortingByOut(cycleSort)} className={styles.sortable}>
                    Out <div className={styles.sortIcon}><SortIcon /></div>
                  </div>
                </th>
                <th>
                  <div onClick={() => setSortingByProfit(cycleSort)} className={styles.sortable}>
                    PROFIT <div className={styles.sortIcon}><SortIcon /></div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers?.map((d, i) => (
                <tr key={i}>
                  <td data-label={config.nameColumnLabel}>
                    <Link to={`/user/${d.id}`} className={styles.link}>{d.name}</Link>
                  </td>
                  <td data-label={config.key === 'player' ? 'Username' : 'Login Account'}>{d.username}</td>
                  <td data-label="Role">{d.role.name}</td>
                  <td data-label="Status">
                    <label className={styles.switch}>
                      <input type="checkbox" checked={d.isActive} readOnly />
                      <span className={styles.slider}></span>
                    </label>
                  </td>
                  {config.showWagerCashbackToggles && (
                    <td data-label="Wager Enabled">
                      <label className={styles.switch}>
                        <input type="checkbox" checked={d.isWagerEnabled || false} onChange={() => handleWagerEnabledChange(d.id)} />
                        <span className={styles.slider}></span>
                      </label>
                    </td>
                  )}
                  {config.showWagerCashbackToggles && (
                    <td data-label="Cashback Enabled">
                      <label className={styles.switch}>
                        <input type="checkbox" checked={d.isCashbackEnabled || false} onChange={() => handleCashbackEnabledChange(d.id)} />
                        <span className={styles.slider}></span>
                      </label>
                    </td>
                  )}
                  {config.showOnlinePaymentToggle && (
                    <td data-label="Online Payment Enabled">
                      <label className={styles.switch}>
                        <input type="checkbox" checked={d.isOnlinePaymentEnabled || false} onChange={() => handleOnlinePaymentEnabledChange(d.id)} />
                        <span className={styles.slider}></span>
                      </label>
                    </td>
                  )}
                  <td data-label="Affiliate ID">
                    <button
                      className={styles.affiliateIdBtn}
                      onClick={() => handleAffiliateIdClick(String(d.id || ''), d.username)}
                      title="Click to generate QR code"
                    >
                      {d.id}
                    </button>
                  </td>
                  <td data-label="Last Login Address">{d.loginIp}</td>
                  <td data-label="Last Login Time">{formatLoginTime(d.lastLogin || '')}</td>
                  {config.showPlayingGameColumn && (
                    <td data-label="Playing Game" className={d.Balance?.onHold ? styles.playingGameCol : styles.notPlayingGameCol}>
                      {d.Balance?.onHold ? 'Yes' : 'No'}
                    </td>
                  )}
                  <td data-label="Balance">
                    {balanceTotalAmount(d.Balance, config.balanceIncludesOnHold)
                      ? formatNumberWithCommas(balanceTotalAmount(d.Balance, config.balanceIncludesOnHold))
                      : '0'}
                  </td>
                  <td className={styles.inCol} data-label="In">
                    {d.balanceIn ? formatNumberWithCommas(d.balanceIn) : '0'}
                  </td>
                  <td className={styles.outCol} data-label="Out">
                    {d.balanceOut ? formatNumberWithCommas(d.balanceOut) : '0'}
                  </td>
                  <td className={styles.profitCol} data-label="PROFIT">
                    {formatNumberWithCommas(
                      (d.balanceIn ? Number(d.balanceIn) : 0) -
                        (d.balanceOut ? Number(d.balanceOut) : 0),
                    )}
                  </td>
                </tr>
              ))}
              <tr className={styles.totalRow}>
                <td style={{ border: '0', background: '#fafafa' }}></td>
                <td style={{ border: '0', background: '#fafafa' }}></td>
                <td style={{ border: '0', background: '#fafafa' }}></td>
                <td style={{ border: '0', background: '#fafafa' }}></td>
                {config.showWagerCashbackToggles && <td style={{ border: '0', background: '#fafafa' }}></td>}
                {config.showWagerCashbackToggles && <td style={{ border: '0', background: '#fafafa' }}></td>}
                {config.showOnlinePaymentToggle && <td style={{ border: '0', background: '#fafafa' }}></td>}
                <td style={{ border: '0', background: '#fafafa' }}></td>
                <td style={{ border: '0', background: '#fafafa' }}></td>
                {/* <td style={{ border: '0', background: '#fafafa' }}></td> */}
                {config.showPlayingGameColumn && <td style={{ border: '0', background: '#fafafa' }}></td>}
                <td className={styles.totalLabel} data-label="TOTAL">TOTAL</td>
                <td data-label="Balance">{formatNumberWithCommas(totals.balance)}</td>
                <td className={styles.inCol} data-label="In">{formatNumberWithCommas(totals.in)}</td>
                <td className={styles.outCol} data-label="Out">{formatNumberWithCommas(totals.out)}</td>
                <td className={styles.profitCol} data-label="PROFIT">{formatNumberWithCommas(totals.profit)}</td>
              </tr>
            </tbody>
          </table>
          <div className={styles.pagination}>
            {total && Math.ceil(total / PAGE_SIZE) > 0 ? (
              <Stack spacing={1}>
                <Pagination
                  onChange={(_e, value) => {
                    setPage(value);
                    setCursors((prev) => {
                      const newCursors = [...prev];
                      newCursors[page] = { ...newCursors[page], after: pageInfo.endCursor };
                      return newCursors;
                    });
                  }}
                  renderItem={(item) => (
                    <PaginationItem
                      {...item}
                      slots={{
                        previous: () => (
                          <button
                            onClick={() => {
                              if (page > 1 && pageInfo.hasPreviousPage) setPage((p) => p - 1);
                            }}
                            disabled={page === 1 || !pageInfo.hasPreviousPage}
                            className={styles.searchBtn + ' ' + styles.paginationBtn}
                          >
                            Prev
                          </button>
                        ),
                        next: () => (
                          <button
                            onClick={() => {
                              if (pageInfo.hasNextPage) {
                                setCursors((prev) => {
                                  const newCursors = [...prev];
                                  newCursors[page] = { ...newCursors[page], after: pageInfo.endCursor };
                                  return newCursors;
                                });
                                setPage((p) => p + 1);
                              }
                            }}
                            disabled={!pageInfo.hasNextPage}
                            className={styles.searchBtn + ' ' + styles.paginationBtn}
                          >
                            Next
                          </button>
                        ),
                      }}
                      sx={{
                        '&.Mui-selected': {
                          backgroundColor: '#313485',
                          color: '#fff',
                          padding: '5px',
                          '&:hover': { backgroundColor: '#232665' },
                        },
                        padding: '5px',
                      }}
                    />
                  )}
                  count={Math.ceil(total / PAGE_SIZE)}
                  page={page}
                  color="primary"
                  size="medium"
                  shape="rounded"
                  siblingCount={siblingCount}
                  boundaryCount={1}
                />
              </Stack>
            ) : (
              <button disabled={page === 1} className={styles.searchBtn + ' ' + styles.paginationBtn}>
                1
              </button>
            )}
          </div>
        </div>
      </TableLoading>

      <QRCodePopup
        isOpen={qrPopupOpen}
        onClose={() => setQrPopupOpen(false)}
        affiliateId={selectedAffiliateId}
        username={selectedUsername}
      />
    </main>
  );
};

export default RoleListPage;
